import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { fetchLastPosts, registerHistoricalRead } from '@/services/streaksApi';
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
    Command,
    CommandEmpty,
    CommandGroup,
    CommandInput,
    CommandItem,
    CommandList,
} from "@/components/ui/command";
import { toast } from 'sonner';
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Check, ChevronsUpDown } from "lucide-react";
import { cn } from "@/lib/utils";

const HistoricalRead: React.FC = () => {
    const [email, setEmail] = useState('');
    const [postId, setPostId] = useState('');
    const [open, setOpen] = useState(false);
    const queryClient = useQueryClient();

    const { data: posts = [], isLoading: postsLoading } = useQuery({
        queryKey: ['last-posts'],
        queryFn: fetchLastPosts
    });

    const registerMutation = useMutation({
        mutationFn: ({ email, postId }: { email: string; postId: string }) =>
            registerHistoricalRead(email, postId),
        onSuccess: () => {
            toast.success('Leitura registrada com sucesso!');
            setEmail('');
            setPostId('');
            queryClient.invalidateQueries({ queryKey: ['streaks'] });
        },
        onError: (error) => {
            toast.error('Erro ao registrar leitura: ' + error.message);
        }
    });

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        if (!email || !postId) {
            toast.error('Por favor, preencha todos os campos');
            return;
        }
        registerMutation.mutate({ email, postId });
    };

    const selectedPost = posts.find((post) => post.postId === postId);

    return (
        <Card>
            <CardHeader>
                <CardTitle className="dark:text-white">Registrar Leitura</CardTitle>
            </CardHeader>
            <CardContent>
                <form onSubmit={handleSubmit} className="space-y-4">
                    <div className="space-y-2">
                        <label className="text-sm font-medium dark:text-gray-300">
                            Email do Usuário
                        </label>
                        <Input
                            type="email"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            placeholder="email@exemplo.com"
                            className="dark:bg-gray-800 dark:text-white"
                        />
                    </div>

                    <div className="space-y-2">
                        <label className="text-sm font-medium dark:text-gray-300">
                            Post
                        </label>
                        <Popover open={open} onOpenChange={setOpen}>
                            <PopoverTrigger asChild>
                                <Button
                                    variant="outline"
                                    role="combobox"
                                    aria-expanded={open}
                                    className="w-full justify-between dark:bg-gray-800 dark:text-white"
                                >
                                    {selectedPost ? `${new Date(selectedPost.publishDate).toLocaleDateString('pt-BR')} - ${selectedPost.title}` : "Selecione um post..."}
                                    <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                                </Button>
                            </PopoverTrigger>
                            <PopoverContent 
                                className="w-full p-0" 
                                align="start"
                                side="bottom"
                                sideOffset={5}
                            >
                                <Command>
                                    <CommandInput placeholder="Pesquisar post..." className="h-9" />
                                    <CommandList>
                                        <CommandEmpty>Nenhum post encontrado.</CommandEmpty>
                                        <CommandGroup>
                                            {posts.map((post) => (
                                                <CommandItem
                                                    key={post.postId}
                                                    value={post.postId}
                                                    onSelect={(_currentValue) => {
                                                        setPostId(post.postId);
                                                        setOpen(false);
                                                    }}
                                                >
                                                    <Check
                                                        className={cn(
                                                            "mr-2 h-4 w-4",
                                                            postId === post.postId ? "opacity-100" : "opacity-0"
                                                        )}
                                                    />
                                                    {post.title}
                                                </CommandItem>
                                            ))}
                                        </CommandGroup>
                                    </CommandList>
                                </Command>
                            </PopoverContent>
                        </Popover>
                    </div>

                    <Button
                        type="submit"
                        disabled={registerMutation.isPending || postsLoading}
                        className="w-full bg-pink-500 hover:bg-pink-600 text-white"
                    >
                        {registerMutation.isPending ? 'Registrando...' : 'Registrar Leitura'}
                    </Button>
                </form>
            </CardContent>
        </Card>
    );
};

export default HistoricalRead; 